import { GlobalSearchEntityType } from 'types/globalSearch';
import type { PinKind } from 'utils/pins';
import { isCanonicalNumericRouteId } from './linkUtil';

/**
 * The one map from a search result's entity family to its detail route.
 *
 * Search returns an entity type and an id and never a URL, because the routes are irregular: the
 * segment is singular for most families (`/matter/:id`, `/contact/:id`) and plural for a few
 * (`/projects/:id`, `/delegations/:id`, `/timecards/:id`), and the family's name is no guide to
 * which. Every entry below is taken from `src/routes/MainRoutes.tsx`; do not pattern a new one from
 * the family name.
 *
 * `PRICELIST` is absent on purpose — see {@link pathForResult}.
 */
const DETAIL_ROUTE_SEGMENT: Record<Exclude<GlobalSearchEntityType, 'PRICELIST'>, string> = {
    MATTER: 'matter',
    INVOICE: 'invoice',
    PARTY: 'contact',
    // A prepayment is an invoice: the prepayments list links its rows to /invoice/:id too.
    PREPAYMENT: 'invoice',
    TIMECARD: 'timecards',
    EFFORT: 'effort',
    EXPENSE: 'expense',
    CORRESPONDENCE: 'correspondence',
    BANK_TRANSACTION: 'bank-transfers',
    CONTRACT: 'contract',
    PROJECT: 'projects',
    EXPERIENCE_RECORD: 'experience-records',
    DEBRIEFING: 'debriefing',
    PARTY_GROUP: 'group',
    EMPLOYEE: 'employee',
    TEAM: 'team',
    OFFICE: 'office',
    PRACTICE_AREA: 'practice-area',
    JOB_POSITION: 'job-position',
    JOB_ROLE: 'job-role',
    DELEGATION: 'delegations',
};

/**
 * The records a link can name that search does not return, by the segment of their detail route, which is also their
 * pin kind. Kept out of {@link DETAIL_ROUTE_SEGMENT}, so {@link resolveRecordFromPath} never reads one as a search
 * result.
 */
export const UNSEARCHED_RECORD_SEGMENT = { TASK: 'task', TRAINING: 'trainings' } as const satisfies Record<
    string,
    PinKind
>;

/** A record a link can name: a search family, or a task or a training. */
export type RecordKind = GlobalSearchEntityType | keyof typeof UNSEARCHED_RECORD_SEGMENT;

/** Where a pricelist is read, there being no `/pricelist/:id` route: the list shows them in full. */
const PRICELISTS_PATH = '/pricelists';

/**
 * Segments that mean the page is a form rather than a record being read. A path containing one is
 * not a view of the record whose id it carries — `/contact/5/matter/new` is a new case, not a visit
 * to contact 5 — so {@link resolveRecordFromPath} declines the whole path.
 */
const FORM_SEGMENTS = new Set(['new', 'edit', 'copy', 'duplicate']);

/**
 * Which family a URL segment belongs to. Two families share `/invoice/:id`; the first declared wins,
 * so a prepayment opened from its own list is remembered as an invoice — which is what its URL says
 * it is, and the only thing the URL can tell us.
 */
const FAMILY_BY_SEGMENT = new Map<string, GlobalSearchEntityType>();
for (const [family, segment] of Object.entries(DETAIL_ROUTE_SEGMENT) as [GlobalSearchEntityType, string][]) {
    if (!FAMILY_BY_SEGMENT.has(segment)) {
        FAMILY_BY_SEGMENT.set(segment, family);
    }
}

/**
 * The in-app path a search result opens, and a record chip.
 *
 * A pricelist has no detail page of its own — it is read inside the pricelists list and on the
 * contract it belongs to — so the list is where its row goes. Every other family has a route that
 * takes the record's id.
 */
export function pathForResult(entityType: RecordKind, id: number): string {
    if (entityType === 'PRICELIST') {
        return PRICELISTS_PATH;
    }
    const segment =
        entityType === 'TASK' || entityType === 'TRAINING'
            ? UNSEARCHED_RECORD_SEGMENT[entityType]
            : DETAIL_ROUTE_SEGMENT[entityType];
    return `/${segment}/${id}`;
}

/** Which record each detail route's segment shows, a task's and a training's included. */
const KIND_BY_SEGMENT = new Map<string, RecordKind>([
    ...FAMILY_BY_SEGMENT,
    ...Object.entries(UNSEARCHED_RECORD_SEGMENT).map(([kind, segment]) => [segment, kind as RecordKind] as const),
]);

/**
 * The record a link's path names: exactly `/<segment>/<id>`, as the shared link helpers build `/${domain}/${id}`.
 * None for a path with a query, a further segment or an id that is not canonical, which a record's own route would
 * not reproduce.
 */
export function recordOfLinkPath(path: string): { entityType: RecordKind; id: number } | null {
    const [, segment = '', id = ''] = /^\/([^/?#]+)\/([^/?#]+)$/.exec(path) ?? [];
    const entityType = KIND_BY_SEGMENT.get(segment);
    return entityType && isCanonicalNumericRouteId(id) ? { entityType, id: Number(id) } : null;
}

/**
 * The results page, for a query and optionally one family of it.
 *
 * The dropdown's "see all" links and its closing row both come here, and the page reads the query
 * back off the URL — which is why the URL and not a piece of state is what carries it.
 */
export function searchResultsPath(query: string, entityType?: GlobalSearchEntityType): string {
    const params = new URLSearchParams({ q: query });
    if (entityType) {
        params.set('entityType', entityType);
    }
    return `/search?${params.toString()}`;
}

/** The same destination as an absolute URL, for "Copy link". */
export function absoluteUrlForResult(entityType: RecordKind, id: number): string {
    return new URL(pathForResult(entityType, id), window.location.origin).toString();
}

/**
 * Which record, if any, a URL is showing — the inverse of {@link pathForResult} for search's families,
 * used to record what the user has opened without a call in every detail page.
 *
 * The last `<family>/<id>` pair wins, so `/matter/7/invoice/12` is the invoice being read rather
 * than the case it hangs off. Ids must be canonical, which keeps `/timecards/draft` and
 * `/contacts/marketing` out, and a form segment anywhere in the path declines it entirely.
 */
export function resolveRecordFromPath(pathname: string): { entityType: GlobalSearchEntityType; id: number } | null {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.some((segment) => FORM_SEGMENTS.has(segment))) {
        return null;
    }

    let found: { entityType: GlobalSearchEntityType; id: number } | null = null;
    for (let index = 0; index < segments.length - 1; index++) {
        const entityType = FAMILY_BY_SEGMENT.get(segments[index]);
        if (entityType && isCanonicalNumericRouteId(segments[index + 1])) {
            found = { entityType, id: Number(segments[index + 1]) };
        }
    }
    return found;
}
